/* ==========================================================================
   The SoArt — Delivery Modal (core)
   soart-modal.js
   --------------------------------------------------------------------------
   Single-view DELIVERY modal for the Shopify Horizon theme.

   Responsibilities of THIS file:
     1. window.SOART namespace + small shared SOART.util helpers.
     2. The <soart-delivery-modal> custom element:
          - open / close, focus trap, Escape, ARIA, mobile-safe scroll lock
          - collecting delivery values → injecting Shopify line item properties
          - replaying Horizon's NATIVE add-to-cart click (no custom AJAX)
     3. Capture-phase interception of any [data-soart-delivery-trigger] button.

   Delivery domain logic (postcode / date / time) lives in sibling modules and
   is called lazily at interaction time (so load order never matters):
          SOART.Postcode  → soart-postcode.js
          SOART.Delivery   → soart-delivery.js

   The modal NEVER performs its own AJAX and NEVER replaces Horizon's product
   form. It intercepts the first click, collects delivery info, injects hidden
   properties[...] inputs into the existing form, then re-fires the real Horizon
   button so <add-to-cart-component>.handleClick runs untouched and the native
   Cart Drawer opens exactly as before.

   Injection only ever removes its OWN [data-soart-prop] inputs, so any other
   product-form line item properties (e.g. a product-level Gift message) are
   preserved.
   ========================================================================== */

(function () {
  'use strict';

  var SOART = (window.SOART = window.SOART || {});
  SOART.version = SOART.version || '2.0.0';

  /* ==== SOART.util — shared helpers (defined once) ===================== */
  SOART.util =
    SOART.util ||
    (function () {
      var FOCUSABLE =
        'a[href], button:not([disabled]), textarea:not([disabled]), ' +
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
        '[tabindex]:not([tabindex="-1"])';

      return {
        qs: function (selector, context) {
          return (context || document).querySelector(selector);
        },
        qsa: function (selector, context) {
          return Array.prototype.slice.call(
            (context || document).querySelectorAll(selector)
          );
        },
        parseList: function (value) {
          if (!value) return [];
          return String(value)
            .split(',')
            .map(function (item) {
              return item.trim();
            })
            .filter(function (item) {
              return item.length > 0;
            });
        },
        getFocusable: function (container) {
          if (!container) return [];
          return Array.prototype.slice
            .call(container.querySelectorAll(FOCUSABLE))
            .filter(function (el) {
              return (
                el.offsetParent !== null &&
                el.getAttribute('aria-hidden') !== 'true'
              );
            });
        }
      };
    })();

  var util = SOART.util;

  /* ==== <soart-delivery-modal> ========================================= */
  var TAG = 'soart-delivery-modal';

  function SoartDeliveryModal() {
    return Reflect.construct(HTMLElement, [], SoartDeliveryModal);
  }

  SoartDeliveryModal.prototype = Object.create(HTMLElement.prototype);
  SoartDeliveryModal.prototype.constructor = SoartDeliveryModal;

  /* ---- Lifecycle -------------------------------------------------------- */
  SoartDeliveryModal.prototype.connectedCallback = function () {
    if (this._initialised) return;
    this._initialised = true;

    this.isOpen = false;
    this.triggerButton = null;
    this.triggerForm = null;
    this.previousFocus = null;

    this._dialog = util.qs('[data-soart-dialog]', this);
    this._closeEls = util.qsa('[data-soart-close]', this);
    this._addBtn = util.qs('[data-soart-add]', this);

    this._onKeydown = this._handleKeydown.bind(this);

    var self = this;

    this._closeEls.forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        self.close({ restoreFocus: true });
      });
    });

    if (this._addBtn) {
      this._addBtn.addEventListener('click', function () {
        self.submit();
      });
    }
  };

  /* ---- Open / close ----------------------------------------------------- */
  SoartDeliveryModal.prototype.open = function (trigger) {
    if (this.isOpen) return;

    /* Supersede any pending close-cleanup from a rapid previous close. */
    this._openToken = (this._openToken || 0) + 1;
    if (this._hideTimer) {
      clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }

    this.triggerButton = trigger || null;
    this.triggerForm = trigger ? trigger.closest('form') : null;
    this.previousFocus = document.activeElement;

    this._lockScroll();

    this.removeAttribute('hidden');
    void this.offsetWidth; /* reflow so the transition runs from hidden */
    this.classList.add('is-open');
    this.isOpen = true;

    /* Wire + reset the delivery fields (date min/max, clear validation). */
    if (SOART.Delivery && typeof SOART.Delivery.mount === 'function') {
      try {
        SOART.Delivery.mount(this);
      } catch (err) {
        if (window.console) console.error('[SoArt] Delivery.mount failed', err);
      }
    }

    this._focusInitial();
    document.addEventListener('keydown', this._onKeydown, true);
  };

  SoartDeliveryModal.prototype.close = function (options) {
    if (!this.isOpen) return;
    options = options || {};

    this.classList.remove('is-open');
    this._unlockScroll();
    document.removeEventListener('keydown', this._onKeydown, true);
    this.isOpen = false;

    var self = this;
    var token = this._openToken;
    if (this._hideTimer) {
      clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }

    var hide = function (event) {
      if (event && event.target !== self) return;
      if (event && event.propertyName && event.propertyName !== 'opacity') return;
      self.removeEventListener('transitionend', hide);
      if (self._hideTimer) {
        clearTimeout(self._hideTimer);
        self._hideTimer = null;
      }
      if (self._openToken === token && !self.isOpen) {
        self.setAttribute('hidden', '');
      }
    };

    this.addEventListener('transitionend', hide);
    this._hideTimer = setTimeout(hide, 400);

    if (options.restoreFocus !== false && this.previousFocus && this.previousFocus.focus) {
      try {
        this.previousFocus.focus();
      } catch (err) {
        /* element may be gone; ignore */
      }
    }
  };

  /* ---- Scroll lock (mobile-safe) --------------------------------------- */
  SoartDeliveryModal.prototype._lockScroll = function () {
    var body = document.body;
    if (body.classList.contains('soart-scroll-lock')) return;
    var scrollY = window.scrollY || window.pageYOffset || 0;
    SOART._lockedScrollY = scrollY;
    body.style.setProperty('--soart-lock-top', '-' + scrollY + 'px');
    body.classList.add('soart-scroll-lock');
  };

  SoartDeliveryModal.prototype._unlockScroll = function () {
    var body = document.body;
    if (!body.classList.contains('soart-scroll-lock')) return;
    body.classList.remove('soart-scroll-lock');
    body.style.removeProperty('--soart-lock-top');
    var y = SOART._lockedScrollY || 0;
    SOART._lockedScrollY = 0;
    window.scrollTo(0, y);
  };

  /* ---- Focus ------------------------------------------------------------ */
  SoartDeliveryModal.prototype._focusInitial = function () {
    var target =
      util.qs('[data-soart-field="postcode"]', this) ||
      util.getFocusable(this._dialog)[0] ||
      this._dialog;
    if (target && target.focus) {
      var t = target;
      requestAnimationFrame(function () {
        try {
          t.focus();
        } catch (err) {
          /* ignore */
        }
      });
    }
  };

  /* ---- Keyboard: Escape + focus trap ----------------------------------- */
  SoartDeliveryModal.prototype._handleKeydown = function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      this.close({ restoreFocus: true });
      return;
    }
    if (event.key !== 'Tab') return;

    var focusable = util.getFocusable(this._dialog);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !this._dialog.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---- Submit-gate API (called by SOART.Delivery) ---------------------- */
  SoartDeliveryModal.prototype.enableSubmit = function () {
    if (this._addBtn) this._addBtn.removeAttribute('disabled');
  };

  SoartDeliveryModal.prototype.disableSubmit = function () {
    if (this._addBtn) this._addBtn.setAttribute('disabled', '');
  };

  /* ---- Collect delivery line item properties --------------------------- */
  SoartDeliveryModal.prototype.collectProperties = function () {
    var props = {};

    var postcodeEl = util.qs('[data-soart-field="postcode"]', this);
    var dateEl = util.qs('[data-soart-field="date"]', this);
    var timeEl = util.qs('[data-soart-field="time"]', this);

    var rawPostcode = postcodeEl ? postcodeEl.value : '';
    var isoDate = dateEl ? dateEl.value : '';
    var slotValue = timeEl ? timeEl.value : '';
    var slotLabel =
      timeEl && timeEl.selectedIndex >= 0
        ? timeEl.options[timeEl.selectedIndex].text.trim()
        : slotValue;

    var prettyPostcode =
      SOART.Postcode && typeof SOART.Postcode.format === 'function'
        ? SOART.Postcode.format(rawPostcode)
        : rawPostcode.trim().toUpperCase();

    var prettyDate =
      SOART.Delivery && typeof SOART.Delivery.formatDate === 'function'
        ? SOART.Delivery.formatDate(isoDate)
        : isoDate;

    /* Required-at-submit fields (Add is only enabled once these are valid). */
    if (prettyPostcode) props['Delivery postcode'] = prettyPostcode;
    if (prettyDate) props['Delivery date'] = prettyDate;
    if (slotLabel) props['Delivery time'] = slotLabel;

    /* Machine-readable mirrors — underscore prefix hides them from cart /
       checkout UI, ready for future capacity / slot-limit logic. */
    if (isoDate) props['_delivery_date'] = isoDate;
    if (slotValue) props['_delivery_time'] = slotValue;

    return props;
  };

  /* ---- Submit: inject properties + replay native Horizon click --------- */
  SoartDeliveryModal.prototype.submit = function () {
    var form = this.triggerForm;
    var button = this.triggerButton;

    if (this._addBtn) this._addBtn.classList.add('is-busy');

    if (form) {
      /* Remove ONLY the inputs this modal injected previously — never touch
         other product-form line item properties. */
      util.qsa('[data-soart-prop]', form).forEach(function (el) {
        el.parentNode.removeChild(el);
      });

      var props = this.collectProperties();
      Object.keys(props).forEach(function (name) {
        var value = props[name];
        if (value === '' || value == null) return;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + name + ']';
        input.value = value;
        input.setAttribute('data-soart-prop', '');
        form.appendChild(input);
      });
    }

    /* Close the modal (leave focus for the Cart Drawer to manage). */
    this.close({ restoreFocus: false });
    if (this._addBtn) this._addBtn.classList.remove('is-busy');

    /* Replay the genuine Horizon button click. The capture interceptor sees
       the pass-through flag and lets the event run to Horizon's handleClick,
       which performs the native AJAX add and opens the Cart Drawer. */
    if (button) {
      button.dataset.soartPass = '1';
      button.click();
    }
  };

  /* ---- Register the element -------------------------------------------- */
  if (!customElements.get(TAG)) {
    customElements.define(TAG, SoartDeliveryModal);
  }
  SOART.Modal = SoartDeliveryModal;

  /* ==== Capture-phase interception of the Add-to-Cart trigger ========== */
  function handleTriggerClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var trigger = target.closest('[data-soart-delivery-trigger]');
    if (!trigger) return;

    /* Pass-through replay: clear the flag and let Horizon's handler run. */
    if (trigger.dataset.soartPass === '1') {
      delete trigger.dataset.soartPass;
      return;
    }

    if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') {
      return;
    }

    var modal = document.querySelector(TAG);
    if (!modal) return; /* fail open — native flow proceeds */

    event.preventDefault();
    event.stopImmediatePropagation();

    if (typeof modal.open === 'function') {
      modal.open(trigger);
    }
  }

  if (!SOART._triggerBound) {
    document.addEventListener('click', handleTriggerClick, true);
    SOART._triggerBound = true;
  }
})();